import { Capacitor } from '@capacitor/core'
let FirebaseAnalytics
try {
  FirebaseAnalytics = require('@capacitor-firebase/analytics').FirebaseAnalytics
} catch (error) {
  console.warn('FirebaseAnalytics not available:', error)
  FirebaseAnalytics = null
}
import { nullSafe, truncateString } from 'Utility/globalMethods'
import { removeHtmlTagsFromString } from 'Utility/globalMethods'
/*
 * This middleware captures actions to push gmt event
 */

let alwaysPresentParams

export const setMenuAnalytics = (label, target_url, level) => {
  window.dataLayer.push({
    event: 'menu_click',
    menu: {
      clicked: {
        first_level: {
          label: level === 1 ? label : null,
          target_url: level === 1 ? target_url : null
        },
        second_level: {
          label: level === 2 ? label : null,
          target_url: level === 2 ? target_url : null
        },
        third_level: {
          label: level === 3 ? label : null,
          target_url: level === 3 ? target_url : null
        }
      }
    },
    ...alwaysPresentParams
  })
}

export const setCtaAnalytics = (text, ctaClass, target_url, id) => {
  window.dataLayer.push({
    event: 'cta_click',
    cta: {
      text: nullSafe(() => text, null),
      class: nullSafe(() => ctaClass, null),
      target_url: nullSafe(() => target_url, null),
      id: nullSafe(() => id, null)
    },
    ...alwaysPresentParams
  })
}

export const setTooltipAnalytics = (message, title) => {
  window.dataLayer.push({
    event: 'tooltip',
    tooltip: {
      message: nullSafe(() => message, null),
      title: nullSafe(() => title, null)
    },
    ...alwaysPresentParams
  })
}

export const setAccordionAnalytics = (action, title) => {
  window.dataLayer.push({
    event: 'accordion',
    accordion: {
      action,
      title
    },
    ...alwaysPresentParams
  });
}

export const setTabSwitchingAnalytics = (label) => {
  const normalizedLabel = typeof label === 'string' ? label.toLowerCase() : label

  window.dataLayer.push({
    event: 'tab_switching',
    tab: {
      label: nullSafe(() => normalizedLabel, null)
    },
    ...alwaysPresentParams
  })
}

export const setPopupClickAnalytics = (title, description, primaryAction, secondaryAction, closeAction, clicked) => {
  window.dataLayer.push({
    event: 'popup',
    popup: {
      action: clicked ? 'click' : 'impression',
      elements: {
        title: truncateString(
          typeof title === 'object' ? nullSafe(() => removeHtmlTagsFromString(title.props.defaultMessage), null) : removeHtmlTagsFromString(title),
          100
        ),
        content: truncateString(
          typeof description === 'object'
            ? nullSafe(() => removeHtmlTagsFromString(description.props.defaultMessage), null)
            : removeHtmlTagsFromString(description) || null,
          100
        ),
        name: null,
        cta: {
          existing:
            primaryAction || secondaryAction || closeAction
              ? [
                ...[
                  typeof primaryAction === 'object'
                    ? nullSafe(() => primaryAction.props.defaultMessage, null)
                    : primaryAction
                ].filter(Boolean),
                ...[
                  typeof secondaryAction === 'object'
                    ? nullSafe(() => secondaryAction.props.defaultMessage, null)
                    : secondaryAction
                ].filter(Boolean),
                ...[
                  typeof closeAction === 'object'
                    ? nullSafe(() => closeAction.props.defaultMessage, null)
                    : closeAction
                ].filter(Boolean)
              ]
              : null,
          clicked: typeof clicked === 'object' ? nullSafe(() => clicked.props.defaultMessage, null) : clicked || null
        }
      }
    },
    ...alwaysPresentParams
  })
}

export const setInputFieldFocusAnalytics = (inputLabel, inputId, inputClass, inputAction) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'input_field_focus',
    input_field: {
      label: nullSafe(() => inputLabel, null),
      id: nullSafe(() => inputId, null),
      class: nullSafe(() => inputClass, null)
    },
    action: nullSafe(() => inputAction, null),
    ...alwaysPresentParams
  })
}

export const setFormInteraction = (action, id, element = '', result = '', message = '') => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'form_interaction',
    form: {
      action,
      id,
      element,
      send: {
        result,
        message
      }
    },
    ...alwaysPresentParams
  })
}

/**
 * @param {!string} action - Set to 'impression' or 'click' (see table below for details)
 * @param {!string} section - Set to the name of the section where the module is located (see table below for details)
 * @param {?number} total - Set to the number of modules present in the section; null for homepage quick links
 * @param {!number} position - Set to the position of the module compared to all modules belonging to its section (e.g., 1,2,3, etc.)
 * @param {!string} title - Set to the module's title text
 * @param {string} message - Optional message parameter for analytics
 */
export const setModuleAnalytics = (action, section, total, position, title, message = null) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'module',
    module: {
      action,
      section,
      total,
      position,
      title,
      message
    },
    ...alwaysPresentParams
  })
}

export const setOutcomeAnalytics = (id, description, code, status, message) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'request_outcome',
    request: {
      id,
      description
    },
    response: {
      code,
      status,
      message: nullSafe(() => message, null)
    },
    ...alwaysPresentParams
  })
}

export const setCustomErrorAnalytics = (type, code, message, field) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'custom_error',
    error: {
      type,
      code,
      message,
      field
    },
    ...alwaysPresentParams
  })
}

export const setFaqAnalytics = (action, keyword, title, results, topic_type, rank, feedback) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'faq',
    action,
    faq: {
      keyword: nullSafe(() => keyword, null),
      title: nullSafe(() => title, null),
      results: nullSafe(() => results, null),
      topic_type: nullSafe(() => topic_type, null),
      rank: nullSafe(() => rank, null),
      feedback: nullSafe(() => feedback, null)
    },
    ...alwaysPresentParams
  })
}

/**
 * Sets download analytics event to track document downloads
 * @param {!string} extension - The extension of the file downloaded, without the dot (e.g., 'pdf')
 * @param {!string} label - The downloaded file name, included the extension (e.g., 'transaction_details_123.pdf')
 */
export const setDownloadAnalytics = (extension, label) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'custom_download',
    download: {
      extension: nullSafe(() => extension, null),
      label: typeof label === 'object'
        ? nullSafe(() => label.props.defaultMessage, null)
        : nullSafe(() => label, null)
    },
    ...alwaysPresentParams
  })
}

export const setConsentAcceptanceAnalytics = (consents) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'consents_acceptance',
    consents: [
      { c3: consents.consent3Value ? 1 : 0 },
      { c4: consents.consent4Value ? 1 : 0 },
      { c5: consents.consent5Value ? 1 : 0 },
    ],
    ...alwaysPresentParams
  })
}

export const setAssistanceBoxClickAnalytics = (title, label, section) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'assistance_box_click',
    action: 'click',
    box: {
      section: nullSafe(() => section, null),
      title: typeof title === 'object' ? nullSafe(() => title.props.defaultMessage.toLowerCase(), null) : title.toLowerCase(),
      label: typeof label === 'object' ? nullSafe(() => label.props.defaultMessage.toLowerCase(), null) : label.toLowerCase()
    },
    ...alwaysPresentParams
  });
};

export const setYouTubeVideoAnalytics = (action, percentage, position, title) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'youtube_video',
    video: {
      action: nullSafe(() => action, null),
      percentage: nullSafe(() => percentage, null),
      position: nullSafe(() => position, null),
      title: nullSafe(() => title, null)
    },
    ...alwaysPresentParams
  })
}

export const setToggleChangeAnalytics = (state, label) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'toggle',
    toggle: {
      state: nullSafe(() => state, null),
      label: nullSafe(() => label, null)
    },
    ...alwaysPresentParams
  })
}

// Variable to store the last error
let lastFailureData = null
let failureTimeout = null

export const analytics = (store) => (next) => (action) => {
  window.dataLayer = window.dataLayer || []
  const globalState = store.getState()

  alwaysPresentParams = {
    language: nullSafe(
      () => globalState.language.currentLanguage,
      (navigator.language || navigator.userLanguage).substr(0, 2)
    ),
    screen_resolution: `${screen.width}x${screen.height}`,
    merchant: {
      id: nullSafe(() => globalState.userProfiling.profiling.merchant.id, null),
      cluster: nullSafe(() => globalState.userProfiling.profiling.merchant.clusterType, null),
      pos_count: nullSafe(() => globalState.userProfiling.profiling.posCount, null),
      store_count: nullSafe(() => globalState.userProfiling.profiling.storeCount, null),
      acquiring_count: nullSafe(() => globalState.userProfiling.profiling.acquiringCount, null),
      mcc: nullSafe(() => globalState.userProfiling.profiling.mccList, null),
      abi: nullSafe(() => globalState.userProfiling.profiling.abiList, null),
    },
    user: {
      id: nullSafe(() => globalState.userProfiling.profiling.user.id, null),
      role: nullSafe(() => globalState.userProfiling.profiling.user.userRole, null),
    }
  }

  if (action.type === '@@router/LOCATION_CHANGE') {
    setTimeout(() => {
      // the timeout is necessary to insert the correct title of the page
      const pagePath = nullSafe(() => action.payload.location.pathname, null)
      window.dataLayer.push({
        event: 'virtual_pageview',
        page: {
          path: pagePath,
          title: window.document.title
        },
        http: {
          response: {
            code: '301'
          }
        },
        ...alwaysPresentParams
      })

      Capacitor.isNativePlatform() && FirebaseAnalytics?.logEvent({
        name: 'screen_view',
        params: { screen_name: pagePath }
      });

    }, 1000)
  }

  if (action.type === '@@Homepage/set_carousel_impression_analytics') {
    const carouselServices = nullSafe(() => action.payload, [])

    carouselServices.forEach((service, index) => {
      setModuleAnalytics(
        'impression',
        'home: commercial carousel',
        carouselServices.length,
        index + 1,
        nullSafe(() => service.title.props.defaultMessage, 'unknown')
      )
    })
  }

  if (action.type === '@@login/iam_login_success') {
    window.dataLayer.push({
      event: 'login',
      login: {
        result: nullSafe(() => action.payload.errorCode, 0) ? 0 : 1,
        message: nullSafe(() => action.payload.errorCode, false) ? action.payload.errorCode : null
      },
      ...alwaysPresentParams
    })
    setCustomErrorAnalytics(
      'backend',
      nullSafe(() => action.response.status, null),
      nullSafe(() => action.payload.errorCode, null),
      null
    )
  }

  // Mobile ROPC login success (native app only — web uses iam_login_success above)
  if (Capacitor.isNativePlatform() && action.type === '@@login/ropc_token_success') {
    window.dataLayer.push({
      event: 'login',
      login: {
        result: 1,
        message: null
      },
      ...alwaysPresentParams
    })
    setCustomErrorAnalytics('backend', null, null, null)
  }

  // Mobile ROPC login failure (native app only)
  // custom_error is handled separately by the /_failure/ handler below
  if (Capacitor.isNativePlatform() && action.type === '@@login/ropc_token_failure') {
    window.dataLayer.push({
      event: 'login',
      login: {
        result: 0,
        message: nullSafe(() => action.error.data.error, null)
      },
      ...alwaysPresentParams
    })
  }

  if (action.type === '@@RegistrationNb/update_consents_success') {
    setConsentAcceptanceAnalytics(nullSafe(() => action.payload.consents, null))
  }

  // _failure error management
  const status = nullSafe(() => action.error.status, null)

  if (/_failure/.test(action.type) && status != 409) {
    // Store the last error data
    lastFailureData = {
      type: 'backend',
      status: status,
      message: nullSafe(() => action.error.type, null),
      timestamp: Date.now()
    }

    // Clear the previous timeout if it exists
    if (failureTimeout) {
      clearTimeout(failureTimeout)
    }

    // Set a timeout to send the analytics if no toast arrives
    failureTimeout = setTimeout(() => {
      if (lastFailureData) {
        setCustomErrorAnalytics(
          lastFailureData.type,
          lastFailureData.status,
          lastFailureData.message,
          null
        )
        lastFailureData = null
      }
    }, 2000) // Wait 2 seconds for a potential toast
  }

  // ERROR_TOAST toast management
  if (action.type === '@@toast/show_toast' && action.key === 'ERROR_TOAST') {
    const toastErrorMessage = nullSafe(() => action.moreInfo.label.props.defaultMessage, 'Generic Error')

    // Clear the previous timeout if it exists
    if (failureTimeout) {
      clearTimeout(failureTimeout)
      failureTimeout = null
    }

    if (lastFailureData && (Date.now() - lastFailureData.timestamp) < 4000) {
      // If there is a recent error (last 4 seconds), combine it with the toast
      setCustomErrorAnalytics(
        lastFailureData.type,           // ← 'backend' from failure
        lastFailureData.status,         // ← status from failure
        toastErrorMessage,              // ← custom message from toast
        null
      )
      lastFailureData = null
    }
  }

  if (action.type === '@@shopPicker/set_shop_selection_success' || action.type === '@@shopPicker/set_dispute_shop_selection_success') {
    const globalState = store.getState()
    const isDisputeSelection = action.type === '@@shopPicker/set_dispute_shop_selection_success'
    const selectorGroups = nullSafe(() => globalState.userProfiling.selectorGroups, [])

    const selectedShops = isDisputeSelection
      ? [{
        labelTop: nullSafe(() => action.response.shopName, null),
        code: nullSafe(() => action.response.shopCode || action.response.coServPoint, null),
        address: nullSafe(() => action.response.address, null),
        shopId: nullSafe(() => action.response.shopId, null)
      }].filter(shop => shop.labelTop || shop.code || shop.address || shop.shopId)
      : selectorGroups
        .flatMap(group => group.shopsList || [])
        .flatMap(shop => shop.shops || [])
        .filter(shop => shop.selected || shop.isSelected)

    window.dataLayer.push({
      event: 'active_store_change',
      shops: {
        data: selectedShops.map(({ labelTop, code, address, shopId }) => ({
          name: labelTop,
          code,
          address,
          shopId
        }))
      },
      ...alwaysPresentParams
    })
  }

  if (action.type === '@@global/set_outcome_analytics') {
    handleOutcomeAnalyticsEvent(action.response, action.description, action.statusCode, action.customResponseMessage)
  }

  return next(action)
}

/**
 * Transforms an array of search and filter items into a specific format.
 * @param {Array} items - The array of search and filter items.
 * @returns {Array} - The transformed array with objects containing lower case type and value.
 * @example
 * const items = [{ search: 'Test' }, { shopType: ['Store', 'Ecommerce'] }];
 * const transformedItems = transformSearchAndFiltersItems(items);
 * // transformedItems: [{ type: 'search', value: 'test' }, { type: 'shopType', value: 'store, ecommerce' }]
 */
const transformSearchAndFiltersItems = (items) => items.map(item => {
  const key = Object.keys(item)[0];
  const value = item[key];
  return { type: key.toLowerCase(), value: Array.isArray(value) ? value.join(', ').toLowerCase() : value.toLowerCase() };
});

/**
 * Sets search and filter analytics data in the dataLayer.
 * @param {Array} items - The array of search and filter items.
 * @example
 * const items = [{ search: 'test' }, { shopType: ['store', 'ecommerce'] }];
 * setSearchAndFiltersAnalytics(items);
 */
export const setSearchAndFiltersAnalytics = (items, totals) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'filter',
    filter: {
      results: totals || 0,
      items: transformSearchAndFiltersItems(items) || []
    },
    ...alwaysPresentParams
  })
}

function handleOutcomeAnalyticsEvent(response, description, responseCode = 1, customResponseMessage) {

  const outcome = response.status === 200 ? 'success' : 'failure'

  const outcomeMessage = customResponseMessage
    ? customResponseMessage
    : response.status === 200 ? 'success' : 'failure'

  const id = parseInt(Math.random() * 9999999)
  const outcomeDescription = `Outcome ${outcome} - ${description}`
  const responseStatus = response.status
  const responseMessage = outcomeMessage

  setOutcomeAnalytics(id, outcomeDescription, responseCode, responseStatus, responseMessage)
}
